import { useMostPurchasedProductIdsQuery } from 'types/graphql';
import { Product } from 'utils/helpers/product';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import { ApolloError } from 'apollo-client';

type UseMostPurchasedProductsParams = {
  dispensaryId: string;
};

type UseMostPurchasedProductsReturn = {
  products: Product[];
  loading: boolean;
  error?: ApolloError;
};

export function useMostPurchasedProducts({
  dispensaryId,
}: UseMostPurchasedProductsParams): UseMostPurchasedProductsReturn {
  const { data: productIdsData, loading: productIdsLoading, error: productIdsError } = useMostPurchasedProductIdsQuery({
    variables: {
      dispensaryId,
    },
    fetchPolicy: `network-only`,
    /*
     * The fetchPolicy of "network-only" is used in order to force the query to always be called due to the current user
     * being checked on the backend. Otherwise the previously cached products will appear.
     */
  });

  const productIds = productIdsData?.getMostPurchasedProducts?.map(({ id }) => id) ?? [];
  const productIdsSet = new Set(productIds);

  const { products, loading: productsLoading, error: productsError } = useDispensaryProducts({
    filters: {
      productIds,
    },
    skip: productIdsLoading || productIdsError instanceof ApolloError || !productIds.length,
  });

  const verifiedPurchasedProducts = products.filter(
    (product) => typeof product.id === 'string' && productIdsSet.has(product.id)
  );

  return {
    products: verifiedPurchasedProducts,
    loading: productIdsLoading || productsLoading,
    error: productIdsError ?? productsError,
  };
}
